import { CommonModule } from "@angular/common";
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy } from "@angular/core";
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from "@angular/material/snack-bar";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

import { OnboardingBaseModalComponent } from "@common/onboarding-base-modal/onboarding-base-modal.component";
import { OnboardingSnackbarData } from "./onboarding-snackbar.models";

/**
 * Onboarding Figma node 20:5882 ("Onboarding Component - Snackbar"), the workbook's
 * "Actionless snackbar" used by H03, S04, S06B, DM03, FU05 and MP05. Renders via the shared
 * OnboardingBaseModalComponent card (headingAlign="left", textDensity="compact") as the "Getting
 * started" eyebrow (9px, caption-small - compact density), heading, text and an optional second
 * paragraph (`data.messageSecondary`, e.g. B01's per-band line) split from the first by the
 * Explanation card's divider line (Figma 20:6938) - and no close button (spec §26.3 /
 * FLIP-342 contract: "No buttons, no close control, not user-dismissible").
 *
 * Opened only via `SnackbarService.openOnboardingSnackbar()`, which queues it FIFO behind any
 * snackbar already open and sets `politeness: "polite"`. Requires `panelClass:
 * ["snackbar-new-panel", "onboarding-snackbar-panel"]`: the first (shared with
 * `SnackbarNewComponent`) neutralises the Material surface's paint, the second (material-theme.scss)
 * stops Material sizing it - at <=480px it would otherwise stretch to the full viewport and leave
 * a strip beside the card - so the card's own default width (2 grid columns in from each side)
 * sizes the snackbar.
 *
 * Timer: the service opens it *without* a MatSnackBar `duration` (Material's can't pause); this
 * component runs `data.duration` (6000ms default) itself from `afterOpened()`, paused while the
 * snackbar is hovered or holds keyboard focus, resumed with the remaining time afterwards, then
 * calls `dismiss()`. The host is focusable (`tabindex="0"`) because nothing inside it is.
 *
 * Optional `data.above` lifts the snackbar to sit just above that coach-mark target (Material's
 * snackbar only offers top/bottom): it pads the bottom of the snackbar's own overlay wrapper,
 * which is pointer-events: none - so, unlike padding or a margin inside the card's surface, it
 * leaves no invisible strip over the target that would swallow taps for the 6 seconds.
 */
@Component({
  selector: "app-onboarding-snackbar",
  standalone: true,
  imports: [CommonModule, OnboardingBaseModalComponent],
  // Block, so the host's box wraps the card exactly (an inline host would span the line box instead).
  host: { tabindex: "0", style: "display: block" },
  template: `
    <app-onboarding-base-modal
      [heading]="data.heading"
      [bodyPrimary]="data.message"
      [bodySecondary]="data.messageSecondary"
      [showDividerBeforeSecondary]="true"
      headingAlign="left"
      textDensity="compact"
      [showClose]="false"
      [showEyebrow]="true"
    ></app-onboarding-base-modal>
  `,
})
export class OnboardingSnackbarComponent implements AfterViewInit, OnDestroy {
  /** Space between the snackbar card and the element it sits above. */
  static readonly GAP_PX = 16;
  /** Onboarding spec §26.3 "Actionless snackbar": show for 6 seconds. */
  static readonly DEFAULT_DURATION_MS = 6000;

  private remainingMs: number;
  private startedAt = 0;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private hovered = false;
  private focused = false;
  private openedSub?: Subscription;

  constructor(
    public snackBarRef: MatSnackBarRef<OnboardingSnackbarComponent>,
    @Inject(MAT_SNACK_BAR_DATA) public data: OnboardingSnackbarData,
    private elementRef: ElementRef<HTMLElement>
  ) {
    this.remainingMs = data.duration ?? OnboardingSnackbarComponent.DEFAULT_DURATION_MS;
  }

  ngAfterViewInit(): void {
    this.removeDialogRole();
    this.positionAboveTarget();
    // Same start point as Material's own `duration` - after the enter animation.
    this.openedSub = this.snackBarRef
      .afterOpened()
      .pipe(take(1))
      .subscribe(() => {
        this.started = true;
        this.resumeTimer();
      });
  }

  ngOnDestroy(): void {
    this.openedSub?.unsubscribe();
    this.clearTimer();
  }

  @HostListener("mouseenter")
  onMouseEnter(): void {
    this.hovered = true;
    this.pauseTimer();
  }

  @HostListener("mouseleave")
  onMouseLeave(): void {
    this.hovered = false;
    this.resumeTimer();
  }

  @HostListener("focusin")
  onFocusIn(): void {
    this.focused = true;
    this.pauseTimer();
  }

  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent): void {
    // Focus moving between elements inside the snackbar isn't leaving it.
    const next = event.relatedTarget as Node | null;
    if (next && this.elementRef.nativeElement.contains(next)) return;
    this.focused = false;
    this.resumeTimer();
  }

  @HostListener("window:resize")
  positionAboveTarget(): void {
    if (!this.data.above) return;
    const target = document.querySelector<HTMLElement>(`[data-flip-coach-target="${this.data.above}"]`);
    const host = this.elementRef.nativeElement;
    const wrapper = host.closest<HTMLElement>(".cdk-global-overlay-wrapper");
    if (!target || !wrapper) return;

    // Material positions the snackbar with GlobalPositionStrategy.bottom('0'), which rewrites the
    // pane's margin-bottom on every updatePosition() (including one right after attach) - so the
    // offset goes on this snackbar's own full-viewport wrapper instead, as bottom padding
    // (pointer-events: none, and the strategy never touches its padding). border-box keeps the
    // wrapper viewport-sized so the padding shrinks the area the pane is flush-bottom within.
    // The card's bottom edge then sits the Material container's own bottom margin above that.
    const container = host.closest<HTMLElement>(".mat-mdc-snack-bar-container");
    const containerMargin = container ? parseFloat(getComputedStyle(container).marginBottom) || 0 : 0;
    const lift = window.innerHeight - target.getBoundingClientRect().top + OnboardingSnackbarComponent.GAP_PX;
    wrapper.style.boxSizing = "border-box";
    wrapper.style.paddingBottom = `${Math.max(0, lift - containerMargin)}px`;
  }

  private pauseTimer(): void {
    if (this.timerId === null) return;
    this.clearTimer();
    this.remainingMs = Math.max(0, this.remainingMs - (Date.now() - this.startedAt));
  }

  private resumeTimer(): void {
    if (!this.started || this.hovered || this.focused || this.timerId !== null) return;
    this.startedAt = Date.now();
    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.snackBarRef.dismiss();
    }, this.remainingMs);
  }

  private clearTimer(): void {
    if (this.timerId === null) return;
    clearTimeout(this.timerId);
    this.timerId = null;
  }

  /**
   * The shared card carries `role="dialog"` + `aria-label` (right for the onboarding modals and
   * anchored prompts), but Material moves this content into its `aria-live` region, and a dialog
   * role there conflicts with the polite status announcement. The base modal has no input for it,
   * so it's stripped from this component's own rendered DOM (a static attribute - Angular never
   * re-applies it).
   */
  private removeDialogRole(): void {
    const card = this.elementRef.nativeElement.querySelector<HTMLElement>(".ob-modal__card");
    card?.removeAttribute("role");
    card?.removeAttribute("aria-label");
  }
}
